import { useNavigate } from "react-router-dom";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { CheckCircle2, Circle, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import { AIAssistantButton } from "@/components/agent";
import { GenieAvatar } from "@/components/GenieAvatar";
import { GenieChannelIndicators } from "@/components/agent/GenieChannelIndicators";
import { Badge } from "@/components/ui/badge";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { useBrain } from "@/contexts/ui/brain";
import { Agent } from "@/types/agent/agent";
import { getGenieTypeMeta } from "@/types/genie-type";
import { getGenieBrandTheme, getReadableGenieBrandTheme } from "@/utils/branding/genieBrandTheme";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { GeniePurposeBadge } from "./GeniePurposeBadge";

interface GenieDetailHeaderProps {
  genie: Agent;
  brand: string;
  hasBranding: boolean;
  isTeamGenie: boolean;
  /** Left out for genies with nothing to talk to, such as a Listener Genie. */
  onQuickTest?: () => void;
  onShare: () => void;
  onSetUpMailbox: () => void;
}

export const GenieDetailHeader = ({
  genie,
  brand,
  hasBranding,
  isTeamGenie,
  onQuickTest,
  onShare,
  onSetUpMailbox,
}: GenieDetailHeaderProps) => {
  const showMailboxNotice = !isTeamGenie && genie.mailbox === null;
  const navigate = useNavigate();
  const { openBrain } = useBrain();
  const typeMeta = getGenieTypeMeta(genie.type);
  const { secondary, gradient, gradientOn, angle } = getReadableGenieBrandTheme(genie);

  const openBrainForGenie = () =>
    openBrain(undefined, { genieId: genie.elevenlabs_id || genie.id, genieName: genie.name });

  const Badges = () => (
    <>
      <GeniePurposeBadge genie={genie} readOnly={isTeamGenie} />
      <Badge
        className="flex items-center gap-1.5 border"
        style={{ borderColor: `${brand}55`, color: brand, backgroundColor: `${brand}10` }}
      >
        <typeMeta.icon className="w-3.5 h-3.5" />
        {typeMeta.label}
      </Badge>
      {genie.is_active ? (
        <Badge className="flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Active
        </Badge>
      ) : (
        <Badge className="flex items-center gap-1.5 bg-gray-100 text-gray-500 border border-gray-200">
          <Circle className="w-3.5 h-3.5" />
          Inactive
        </Badge>
      )}
      {!genie.is_public && (
        <Badge className="flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200">
          <Lock className="w-3.5 h-3.5" />
          Private
        </Badge>
      )}
      {genie.is_setup && <Badge className="bg-primary/10 text-primary border border-primary/20">Setup Genie</Badge>}
      {isTeamGenie && (
        <Badge className="bg-gray-100 text-gray-700 border border-gray-200">
          <CopyableEmail email={genie.owner?.full_name || genie.owner?.email} fallback="Team" />
        </Badge>
      )}
      <GenieChannelIndicators genie={genie} brand={brand} className="md:justify-end" />
    </>
  );

  return (
    <div
      className="relative z-0 bg-white rounded-2xl shadow-lg overflow-hidden mb-6 border-2"
      style={{
        ...(hasBranding
          ? {
              borderColor: brand,
              boxShadow: `0 10px 15px -3px ${brand}20, 0 4px 6px -2px ${brand}10`,
            }
          : { borderColor: "#E5E7EB" }),
      }}
    >
      <div className="px-6 md:px-8 py-6">
        {/* Mobile: badges + AI button on top */}
        <div className="flex md:hidden items-center justify-between gap-2 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badges />
          </div>
          <AIAssistantButton onClick={openBrainForGenie} compact />
        </div>

        {/* Desktop: two-column layout */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Col 1: Avatar + name + description + action buttons */}
          <div className="flex flex-col items-center md:flex-row md:items-center gap-6 md:gap-16 flex-1 min-w-0">
            <div
              className={`flex-shrink-0 relative group ${onQuickTest ? "cursor-pointer" : ""}`}
              onClick={onQuickTest}
              title={onQuickTest ? "Quick test this genie" : undefined}
            >
              <GenieAvatar
                color={getGenieBrandTheme(genie).primary}
                secondaryColor={genie.branding?.secondaryColor}
                gradientEnabled={genie.branding?.gradientEnabled}
                gradientAngle={genie.branding?.gradientAngle}
                avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
                speaking={false}
                listening={false}
                isConnecting={false}
                customAvatarUrl={resolveGenieAvatarUrl(genie)}
                hasCustomBranding={hasBranding}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-full transition-all duration-200" />

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const urlName = genie.url_name ?? genie.page?.url_name;
                  if (urlName) {
                    window.open(`/live-agent/${urlName}`, "_blank");
                  } else {
                    toast.error("Unable to open live page");
                  }
                }}
                className="absolute bottom-0 right-0 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:scale-110"
                style={{ background: gradient }}
                title="Open live page in new tab"
                aria-label="Open live page in new tab"
              >
                <ExternalLink className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-center md:text-left mb-4">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{genie.name}</h1>
                <p className="text-base text-gray-600">
                  {genie.welcome_message || genie.description || "No description available"}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                {onQuickTest && (
                  <button
                    onClick={onQuickTest}
                    className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    style={{ background: gradient }}
                  >
                    <GenieIcon name="chat-play" size={18} color="white" />
                    Quick Test
                  </button>
                )}
                <button
                  onClick={onShare}
                  className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105"
                  style={{ borderColor: brand, color: brand }}
                >
                  <GenieIcon
                    name="share"
                    size={18}
                    color={brand}
                    secondaryColor={secondary}
                    gradientEnabled={gradientOn}
                    gradientAngle={angle}
                  />
                  Share
                </button>
                <button
                  onClick={() => navigate("/genie-hub/brand-integrity")}
                  className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105"
                  style={{ borderColor: brand, color: brand }}
                >
                  <GenieIcon
                    name="shield-check"
                    size={18}
                    color={brand}
                    secondaryColor={secondary}
                    gradientEnabled={gradientOn}
                    gradientAngle={angle}
                  />
                  Genie Trust Center
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/genie-hub/genie-pages/${genie.id}`)}
                  className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 rounded-xl font-semibold hover:bg-gray-50 transition-all hover:scale-105"
                  style={{ borderColor: brand, color: brand }}
                >
                  <GenieIcon
                    name="web-page"
                    size={18}
                    color={brand}
                    secondaryColor={secondary}
                    gradientEnabled={gradientOn}
                    gradientAngle={angle}
                  />
                  Genie Pages
                </button>
              </div>
            </div>
          </div>

          {/* Col 2: badges top, AI button bottom — desktop only */}
          <div className="hidden md:flex flex-col justify-between items-end gap-4 flex-shrink-0 w-48">
            <div className="flex flex-col items-end gap-2">
              <Badges />
            </div>
            <AIAssistantButton onClick={openBrainForGenie} />
          </div>
        </div>
      </div>

      {showMailboxNotice && (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 md:px-8 py-3 border-t"
          style={{ backgroundColor: `${brand}10`, borderColor: `${brand}20` }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <GenieIcon
              name="mail"
              size={28}
              color={brand}
              secondaryColor={secondary}
              gradientEnabled={gradientOn}
              gradientAngle={angle}
              className="flex-shrink-0"
            />
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-gray-900">This genie doesn't have a mailbox yet.</span> Set one up
              to give it its own email address.
            </p>
          </div>
          <button
            type="button"
            onClick={onSetUpMailbox}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors hover:brightness-95 flex-shrink-0"
            style={{ backgroundColor: `${brand}15`, color: brand }}
          >
            Set up mailbox
          </button>
        </div>
      )}
    </div>
  );
};
